import { create } from "zustand";
import {
  authService,
  UpdateProfileDto,
  ChangePasswordDto,
} from "@/services/authService";
import type { User } from "@/types/auth";

interface AuthState {
  // Core state
  user: User | null;
  isAuthenticated: boolean;
  isInitialized: boolean;
  isLoading: boolean;
  error: string | null;

  // Auth operations
  login: (email: string, password: string) => Promise<User>;
  logout: () => Promise<void>;

  // Profile operations
  updateProfile: (data: UpdateProfileDto) => Promise<User>;
  changePassword: (data: ChangePasswordDto) => Promise<void>;

  // Initialization
  initialize: () => Promise<void>;

  // Sync operations
  setUser: (user: User | null) => void;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  // Initial state
  user: null,
  isAuthenticated: false,
  isInitialized: false,
  isLoading: false,
  error: null,

  // Initialize - check if user is already logged in
  initialize: async () => {
    const state = get();

    // Don't initialize twice
    if (state.isInitialized) {
      return;
    }

    set({ isLoading: true });

    try {
      const user = await authService.getMe();
      set({
        user,
        isAuthenticated: true,
        isInitialized: true,
        isLoading: false,
        error: null,
      });
    } catch {
      // Silent fail on initialization - user not logged in
      set({
        user: null,
        isAuthenticated: false,
        isInitialized: true,
        isLoading: false,
        error: null,
      });
    }
  },

  // Login
  login: async (email: string, password: string) => {
    set({ isLoading: true, error: null });

    try {
      const response = await authService.login({ email, password });
      set({
        user: response.user,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });
      return response.user;
    } catch (error) {
      const message = error instanceof Error ? error.message : "Login failed";
      set({
        error: message,
        isLoading: false,
      });
      throw error;
    }
  },

  // Logout
  logout: async () => {
    set({ isLoading: true });

    try {
      await authService.logout();
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      });
    } catch (error) {
      // Force logout even if API call fails
      set({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      });
      throw error;
    }
  },

  // Update profile
  updateProfile: async (data: UpdateProfileDto) => {
    set({ isLoading: true, error: null });

    try {
      const user = await authService.updateProfile(data);
      set({
        user,
        isLoading: false,
        error: null,
      });
      return user;
    } catch (error) {
      const message = error instanceof Error ? error.message : "Update failed";
      set({
        error: message,
        isLoading: false,
      });
      throw error;
    }
  },

  // Change password
  changePassword: async (data: ChangePasswordDto) => {
    set({ isLoading: true, error: null });

    try {
      await authService.changePassword(data);
      set({
        isLoading: false,
        error: null,
      });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Password change failed";
      set({
        error: message,
        isLoading: false,
      });
      throw error;
    }
  },

  // Sync operations
  setUser: (user: User | null) => {
    set({ user, isAuthenticated: !!user });
  },

  clearError: () => {
    set({ error: null });
  },
}));

// Export initialize function for use in AuthProvider
export async function initializeAuth() {
  return useAuthStore.getState().initialize();
}

// Typed selectors to prevent over-subscription
export const useAuthUser = () => useAuthStore((s) => s.user);
export const useAuthIsAuthenticated = () =>
  useAuthStore((s) => s.isAuthenticated);
export const useAuthIsInitialized = () => useAuthStore((s) => s.isInitialized);
export const useAuthIsLoading = () => useAuthStore((s) => s.isLoading);
export const useAuthError = () => useAuthStore((s) => s.error);
export const useAuthActions = () =>
  useAuthStore((s) => ({
    login: s.login,
    logout: s.logout,
    updateProfile: s.updateProfile,
    changePassword: s.changePassword,
    initialize: s.initialize,
    setUser: s.setUser,
    clearError: s.clearError,
  }));
